import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { chevronRightString } from "@insite/mobius/Icons/ChevronRight";
import Phone from "@insite/mobius/Icons/Phone";
import Img from "@insite/mobius/Img/Img";
import { GridContainer, GridItem, Icon, Link } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    isSessionCreated: state.context.session?.userRoles,
    customSettings: getSettingsCollection(state).customSettings,
});
type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;
// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    headingText = "headingText",
    showBillTrustButton = "showBillTrustButton",
    payMyBillText = "payMyBillText",
    openLinkInNewWindow = "openLinkInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showBillTrustButton]: boolean;
        [fields.payMyBillText]: string;
        [fields.openLinkInNewWindow]: boolean;
    };
}

export interface DsgNeedHelpStyles {
    welComeContainer?: GridContainerProps;
    needHelpcontainer?: GridContainerProps;
    needHelpGridItem?: GridItemProps;
    btnLink?: GridItemProps;
    headingText?: TypographyPresentationProps;
}

export const dsgNeedHelpStyles: DsgNeedHelpStyles = {
    welComeContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            [class*="GridItemStyle"] {
                                padding: 0 15px 9px;
                                display: block;
                                width: 100%;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    needHelpcontainer: {
        css: css`
            [class*="GridOffset"] {
                flex-direction: row;
                flex-wrap: nowrap;
                justify-content: space-between;
                align-items: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-direction: column;
                                flex-wrap: wrap;
                                align-items: flex-start;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    btnLink: {
        width: 12,
        css: css`
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: flex-end;
            .icon-right {
                &:after {
                    left: calc(100% - 40px);
                }
                flex-width: 100%;
                max-width: 100%;
                justify-content: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                display: block;
                                width: 100%;
                                text-align: center;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    needHelpGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 8px;
        `,
    },
    headingText: {
        css: css`
            margin: 0;
            font: ${headlineFonts.headline2SemiBoldSm.fontWeight} ${headlineFonts.headline2SemiBoldSm.fontSize}/${headlineFonts.headline2SemiBoldSm.lineHeight} ${headlineFonts.headline2SemiBoldSm.fontFamily};
            color: ${colorPallete.grey800};
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
                        line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
                    `,
                ],
                "min",
            )}
        `,
    },
};

const styles = dsgNeedHelpStyles;

const DsgNeedHelp: React.FC<Props> = ({ session, fields, isSessionCreated, customSettings }) => {
    const impersonateUserId = getCookie("ImpersonateBy") ? getCookie("ImpersonateBy") : "";
    let isEnablePayBillTrust = false;
    const setting = customSettings as any;
    const billTrustUrl = setting ? setting.billTrustLoginLink : "";
    if (isSessionCreated != null && isSessionCreated !== undefined && isSessionCreated.indexOf("Billtrust") > -1) {
        isEnablePayBillTrust = true;
    }
    const consolUserAllowedList = setting? setting.billtrustAllowedUsers.toLocaleLowerCase().split(',') : "";
    let isConsoleUserAllowedForBillTrust = false;
    if (consolUserAllowedList && consolUserAllowedList.length > 0) {
        isConsoleUserAllowedForBillTrust = consolUserAllowedList.includes(impersonateUserId?.toLocaleLowerCase());
    }
    
    return (
        <>
            {fields.showBillTrustButton && (
                <StyledWrapper {...styles.welComeContainer}>
                    <GridContainer {...styles.needHelpcontainer}>
                        <GridItem className="welcome-msg-container" {...styles.needHelpGridItem}>
                            <Typography as="h2" {...styles.headingText}>
                                {`${translate("Welcome Back")}, ${session.firstName}`}
                            </Typography>
                        </GridItem>
                        {isEnablePayBillTrust && (!impersonateUserId || isConsoleUserAllowedForBillTrust) && billTrustUrl && billTrustUrl.length > 0 && (
                            <GridItem {...styles.btnLink}>
                                <a
                                    href={billTrustUrl}
                                    target={fields.openLinkInNewWindow ? "_blank" : ""}
                                    className="btn btn-secondary icon-right"
                                    rel="noreferrer"
                                >
                                    {fields.payMyBillText}
                                </a>
                            </GridItem>
                        )}
                    </GridContainer>
                </StyledWrapper>
            )}
            {!fields.showBillTrustButton && (!impersonateUserId || isConsoleUserAllowedForBillTrust) && (
                <StyledWrapper className="list-wrapper">
                    <StyledWrapper className="links-wrapper">
                        {isEnablePayBillTrust && billTrustUrl && billTrustUrl.length > 0 && (
                            <Link
                                className="list-link"
                                href={billTrustUrl}
                                target={fields.openLinkInNewWindow ? "_blank" : ""}
                            >
                                {fields.payMyBillText}
                            </Link>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            fieldType: "General",
            displayName: "Pay My Bill Text",
            name: fields.payMyBillText,
            editorTemplate: "TextField",
            defaultValue: "",
            sortOrder: 1,
        },
        {
            name: fields.showBillTrustButton,
            displayName: "Display as Button",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 2,
        },
        {
            name: fields.openLinkInNewWindow,
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(DsgNeedHelp)),
    definition,
};

export default widgetModule;
